import { onlineManager, QueryClient, QueryKey } from '@tanstack/react-query'

// WKWebView's navigator.onLine is unreliable (see main.tsx). onlineManager is
// wired to Capacitor's Network plugin on native platforms, so it reflects the
// real connectivity status.
const isOnline = () => onlineManager.isOnline()

type InfiniteQueryOptions<T> = {
  queryKey: QueryKey
  queryFn: (ctx: { pageParam: number; signal?: AbortSignal }) => Promise<T>
  initialPageParam: number
}

/**
 * Cache-first query helper for route loaders.
 *
 * Always returns cached data instantly when available — the page renders
 * immediately from cache while React Query refetches in the background
 * (via staleTime / refetchOnMount from withOfflineMode). When offline or
 * on error, cached data is the final result.
 *
 * @param queryClient - The React Query client
 * @param queryKey - The query key
 * @param queryFn - The query function
 * @returns Cached or fetched data, or undefined if offline with no cache
 */
export async function offlineSafeEnsureQueryData<T>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  queryFn: () => Promise<T>,
): Promise<T | undefined> {
  const cachedData = queryClient.getQueryData<T>(queryKey)

  // Cache hit: return immediately — background refetch handled by React Query
  if (cachedData !== undefined) {
    return cachedData
  }

  // No cache + offline: nothing to show
  if (!isOnline()) {
    return undefined
  }

  // No cache + online: must fetch before the page can render
  try {
    return await queryClient.ensureQueryData({ queryKey, queryFn })
  } catch (error) {
    // Fetch failed but we already checked there's no cache — re-throw
    throw error
  }
}

/**
 * Check if we're offline and have no cached data for a query.
 * Useful for early-exit in loaders.
 */
export function isOfflineWithNoCache<T>(queryClient: QueryClient, queryKey: QueryKey): boolean {
  if (isOnline()) return false
  const cachedData = queryClient.getQueryData<T>(queryKey)
  return !cachedData
}

/**
 * Infinite-query variant of {@link offlineSafeEnsureQueryData}.
 *
 * Returns cached data when present, skips network when offline with no cache,
 * otherwise fetches and re-throws on failure (callers can `.catch()` per-item
 * to allow sibling fetches in a `Promise.all` to continue).
 */
export async function offlineSafeEnsureInfiniteQueryData<T>(
  queryClient: QueryClient,
  queryOptions: InfiniteQueryOptions<T>,
): Promise<unknown> {
  const cachedData = queryClient.getQueryData(queryOptions.queryKey)
  if (cachedData !== undefined) return cachedData
  if (!isOnline()) return undefined

  return await queryClient.ensureInfiniteQueryData(queryOptions)
}
